/**
 * Effects Favorites Panel
 * Quick-access panel for frequently used amp models and pedal effects
 * Persists to localStorage and syncs with user preferences
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  StarOff, 
  Zap, 
  Music, 
  Flame, 
  Guitar, 
  Volume2, 
  Waves,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

// Amp model icons mapping
const AMP_ICONS = {
  clean: Music,
  crunch: Zap,
  highGain: Flame,
  metal: Guitar,
  acoustic: Music,
  britishStack: Zap,
  vintageDeluxe: Music,
  modernMetal: Flame,
  bassDriver: Volume2,
  jazzClean: Music,
  bluesBreaker: Waves,
  matchless: Music,
  dualRecto: Flame,
};

// Pedal category icons
const CATEGORY_ICONS = {
  drive: Flame,
  filter: Waves,
  modulation: Sparkles,
  pitch: Music,
  time: Clock,
  dynamics: Volume2,
  utility: Zap,
};

// Local storage key
const FAVORITES_KEY = 'stringsauce_effect_favorites';
const RECENT_KEY = 'stringsauce_recent_effects';
const MAX_RECENT = 10;

/**
 * Hook for managing effect favorites
 */
export const useEffectFavorites = (userId = 'local') => {
  const [favorites, setFavorites] = useState({ amps: [], pedals: [] });
  const [recentEffects, setRecentEffects] = useState({ amps: [], pedals: [] });
  
  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`${FAVORITES_KEY}_${userId}`);
      if (stored) {
        setFavorites(JSON.parse(stored));
      }
      
      const recentStored = localStorage.getItem(`${RECENT_KEY}_${userId}`);
      if (recentStored) {
        setRecentEffects(JSON.parse(recentStored));
      }
    } catch (e) {
      console.error('Error loading effect favorites:', e);
    }
  }, [userId]);
  
  // Save favorites
  const saveFavorites = useCallback((newFavorites) => {
    setFavorites(newFavorites);
    try {
      localStorage.setItem(`${FAVORITES_KEY}_${userId}`, JSON.stringify(newFavorites));
    } catch (e) {
      console.error('Error saving favorites:', e);
    }
  }, [userId]);
  
  // Save recent effects
  const saveRecent = useCallback((newRecent) => {
    setRecentEffects(newRecent);
    try {
      localStorage.setItem(`${RECENT_KEY}_${userId}`, JSON.stringify(newRecent));
    } catch (e) {
      console.error('Error saving recent effects:', e);
    }
  }, [userId]);
  
  // Toggle amp favorite
  const toggleAmpFavorite = useCallback((ampId) => {
    const isCurrentlyFavorite = favorites.amps.includes(ampId);
    const newAmps = isCurrentlyFavorite
      ? favorites.amps.filter(id => id !== ampId)
      : [...favorites.amps, ampId];
    
    saveFavorites({ ...favorites, amps: newAmps });
    
    if (!isCurrentlyFavorite) {
      toast.success('Added to favorites');
    }
  }, [favorites, saveFavorites]);
  
  // Toggle pedal favorite
  const togglePedalFavorite = useCallback((pedalId) => {
    const isCurrentlyFavorite = favorites.pedals.includes(pedalId);
    const newPedals = isCurrentlyFavorite
      ? favorites.pedals.filter(id => id !== pedalId)
      : [...favorites.pedals, pedalId];
    
    saveFavorites({ ...favorites, pedals: newPedals });
    
    if (!isCurrentlyFavorite) {
      toast.success('Added to favorites');
    }
  }, [favorites, saveFavorites]);
  
  // Record effect usage (for recent/frequent)
  const recordEffectUsage = useCallback((type, effectId) => {
    const key = type === 'amp' ? 'amps' : 'pedals';
    const current = recentEffects[key] || [];
    
    // Remove if exists, add to front
    const filtered = current.filter(id => id !== effectId);
    const newRecent = [effectId, ...filtered].slice(0, MAX_RECENT);
    
    saveRecent({ ...recentEffects, [key]: newRecent });
  }, [recentEffects, saveRecent]);
  
  // Check if effect is favorite
  const isAmpFavorite = useCallback((ampId) => {
    return favorites.amps.includes(ampId);
  }, [favorites.amps]);
  
  const isPedalFavorite = useCallback((pedalId) => {
    return favorites.pedals.includes(pedalId);
  }, [favorites.pedals]);
  
  return {
    favorites,
    recentEffects,
    toggleAmpFavorite,
    togglePedalFavorite,
    recordEffectUsage,
    isAmpFavorite,
    isPedalFavorite,
  };
};

/**
 * Effects Favorites Panel Component
 */
export const EffectsFavoritesPanel = ({ 
  ampModels, 
  pedalTypes,
  onSelectAmp,
  onSelectPedal,
  isExpanded = true,
  onToggleExpand,
  currentAmpModel,
  activePedals = [],
}) => {
  const { 
    favorites, 
    recentEffects, 
    toggleAmpFavorite, 
    togglePedalFavorite,
    isAmpFavorite,
    isPedalFavorite,
    recordEffectUsage,
  } = useEffectFavorites();
  
  const [activeTab, setActiveTab] = useState('favorites'); // favorites, recent
  
  // Get favorite amp models
  const favoriteAmps = useMemo(() => {
    return ampModels.filter(amp => favorites.amps.includes(amp.id));
  }, [ampModels, favorites.amps]);
  
  // Get favorite pedals
  const favoritePedals = useMemo(() => {
    return pedalTypes.filter(pedal => favorites.pedals.includes(pedal.id));
  }, [pedalTypes, favorites.pedals]);
  
  // Get recent amps
  const recentAmps = useMemo(() => {
    return (recentEffects.amps || [])
      .map(id => ampModels.find(amp => amp.id === id))
      .filter(Boolean)
      .slice(0, 5);
  }, [ampModels, recentEffects.amps]);
  
  // Get recent pedals
  const recentPedals = useMemo(() => {
    return (recentEffects.pedals || [])
      .map(id => pedalTypes.find(pedal => pedal.id === id))
      .filter(Boolean)
      .slice(0, 5);
  }, [pedalTypes, recentEffects.pedals]);
  
  // Handle amp selection
  const handleSelectAmp = useCallback((amp) => {
    onSelectAmp?.(amp.id);
    recordEffectUsage('amp', amp.id);
  }, [onSelectAmp, recordEffectUsage]);
  
  // Handle pedal selection
  const handleSelectPedal = useCallback((pedal) => {
    onSelectPedal?.(pedal.id);
    recordEffectUsage('pedal', pedal.id);
  }, [onSelectPedal, recordEffectUsage]);
  
  if (!isExpanded) {
    return (
      <button
        onClick={onToggleExpand}
        className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 border border-zinc-700/50 rounded-lg hover:bg-zinc-800 transition-colors"
        data-testid="favorites-expand-btn"
      >
        <Star className="w-4 h-4 text-amber-400" />
        <span className="text-xs text-zinc-400">Quick Effects</span>
        <ChevronDown className="w-3 h-3 text-zinc-500" />
      </button>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-700/50 rounded-lg overflow-hidden"
      data-testid="favorites-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700/50">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-white">Quick Effects</span>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Tabs */}
          <button
            onClick={() => setActiveTab('favorites')}
            className={`px-2 py-1 text-xs rounded transition-colors ${
              activeTab === 'favorites' 
                ? 'bg-amber-500/20 text-amber-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Star className="w-3 h-3 inline mr-1" />
            Favorites
          </button>
          <button
            onClick={() => setActiveTab('recent')}
            className={`px-2 py-1 text-xs rounded transition-colors ${
              activeTab === 'recent' 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Clock className="w-3 h-3 inline mr-1" />
            Recent
          </button>
          
          <button
            onClick={onToggleExpand}
            className="p-1 ml-2 text-zinc-500 hover:text-white rounded"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-3 space-y-3">
        {activeTab === 'favorites' ? (
          <>
            {/* Favorite Amps */}
            {favoriteAmps.length > 0 && (
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Amp Models
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {favoriteAmps.map(amp => {
                    const Icon = AMP_ICONS[amp.id] || Music;
                    const isActive = currentAmpModel === amp.id;
                    
                    return (
                      <button
                        key={amp.id}
                        onClick={() => handleSelectAmp(amp)}
                        className={`group relative flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] transition-all ${
                          isActive
                            ? `bg-${amp.color}-500/30 border border-${amp.color}-500/50 text-${amp.color}-400`
                            : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600'
                        }`}
                        data-testid={`fav-amp-${amp.id}`}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{amp.name}</span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAmpFavorite(amp.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          role="button"
                        >
                          <X className="w-3 h-3 text-zinc-500 hover:text-red-400" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Favorite Pedals */}
            {favoritePedals.length > 0 && (
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Pedal Effects
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {favoritePedals.map(pedal => {
                    const isActive = activePedals.some(p => p.type === pedal.id);
                    
                    return (
                      <button
                        key={pedal.id}
                        onClick={() => handleSelectPedal(pedal)}
                        disabled={isActive}
                        className={`group relative flex items-center gap-1 px-2 py-1.5 rounded text-[10px] transition-all ${
                          isActive
                            ? 'bg-zinc-800/50 text-zinc-600 cursor-not-allowed'
                            : `bg-${pedal.color}-500/20 border border-${pedal.color}-500/40 text-${pedal.color}-400 hover:bg-${pedal.color}-500/30`
                        }`}
                        data-testid={`fav-pedal-${pedal.id}`}
                      >
                        <span>{pedal.name}</span>
                        {isActive && <span className="text-[8px]">(active)</span>}
                        {!isActive && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePedalFavorite(pedal.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            role="button"
                          >
                            <X className="w-3 h-3 text-zinc-500 hover:text-red-400" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Empty state */}
            {favoriteAmps.length === 0 && favoritePedals.length === 0 && (
              <div className="text-center py-4">
                <StarOff className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-xs text-zinc-500">No favorites yet</p>
                <p className="text-[10px] text-zinc-600 mt-1">
                  Click the star icon on effects to add them here
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Recent Amps */}
            {recentAmps.length > 0 && (
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Recent Amps
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentAmps.map(amp => {
                    const Icon = AMP_ICONS[amp.id] || Music;
                    const isActive = currentAmpModel === amp.id;
                    const isFavorite = isAmpFavorite(amp.id);
                    
                    return (
                      <button
                        key={amp.id}
                        onClick={() => handleSelectAmp(amp)}
                        className={`group relative flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] transition-all ${
                          isActive
                            ? `bg-${amp.color}-500/30 border border-${amp.color}-500/50 text-${amp.color}-400`
                            : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600'
                        }`}
                        data-testid={`recent-amp-${amp.id}`}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{amp.name}</span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAmpFavorite(amp.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          role="button"
                        >
                          <Star className={`w-3 h-3 ${isFavorite ? 'text-amber-400 fill-amber-400' : 'text-zinc-500 hover:text-amber-400'}`} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Recent Pedals */}
            {recentPedals.length > 0 && (
              <div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Recent Pedals
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentPedals.map(pedal => {
                    const isActive = activePedals.some(p => p.type === pedal.id);
                    const isFavorite = isPedalFavorite(pedal.id);
                    
                    return (
                      <button
                        key={pedal.id}
                        onClick={() => handleSelectPedal(pedal)}
                        disabled={isActive}
                        className={`group relative flex items-center gap-1 px-2 py-1.5 rounded text-[10px] transition-all ${
                          isActive
                            ? 'bg-zinc-800/50 text-zinc-600 cursor-not-allowed'
                            : `bg-${pedal.color}-500/20 border border-${pedal.color}-500/40 text-${pedal.color}-400 hover:bg-${pedal.color}-500/30`
                        }`}
                        data-testid={`recent-pedal-${pedal.id}`}
                      >
                        <span>{pedal.name}</span>
                        {!isActive && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePedalFavorite(pedal.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            role="button"
                          >
                            <Star className={`w-3 h-3 ${isFavorite ? 'text-amber-400 fill-amber-400' : 'text-zinc-500 hover:text-amber-400'}`} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Empty state */}
            {recentAmps.length === 0 && recentPedals.length === 0 && (
              <div className="text-center py-4">
                <Clock className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-xs text-zinc-500">No recent effects</p>
                <p className="text-[10px] text-zinc-600 mt-1">
                  Effects you use will appear here
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

/**
 * Inline favorite toggle button for amp/pedal selectors
 * Uses span when inline=true to avoid nested button HTML issues
 */
export const FavoriteToggleButton = ({ 
  effectId, 
  effectType = 'amp', // 'amp' or 'pedal'
  size = 'sm',
  inline = false, // Set to true when inside another button
}) => {
  const { isAmpFavorite, isPedalFavorite, toggleAmpFavorite, togglePedalFavorite } = useEffectFavorites();
  
  const isFavorite = effectType === 'amp' ? isAmpFavorite(effectId) : isPedalFavorite(effectId);
  const toggleFavorite = effectType === 'amp' ? toggleAmpFavorite : togglePedalFavorite;
  
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };
  
  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleFavorite(effectId);
  };
  
  const content = (
    <Star 
      className={`${sizeClasses[size]} transition-colors ${
        isFavorite 
          ? 'text-amber-400 fill-amber-400' 
          : 'text-zinc-500 hover:text-amber-400'
      }`} 
    />
  );
  
  // Use span when inline to avoid nested buttons
  if (inline) {
    return (
      <span
        onClick={handleClick}
        className="p-0.5 rounded hover:bg-zinc-700/50 transition-colors cursor-pointer"
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        data-testid={`favorite-toggle-${effectType}-${effectId}`}
        role="button"
        tabIndex={0}
      >
        {content}
      </span>
    );
  }
  
  return (
    <button
      onClick={handleClick}
      className="p-0.5 rounded hover:bg-zinc-700/50 transition-colors"
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      data-testid={`favorite-toggle-${effectType}-${effectId}`}
    >
      {content}
    </button>
  );
};

export default EffectsFavoritesPanel;
